/* eslint-disable */
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationActions } from '../../actions/application.actions';
import { FlowManagerService } from './flowmanager.service';
import { Routes } from '../models/Enum/Routes';
import * as _ from 'lodash';

@Injectable()
export class DTMService {

  constructor(
    route: ActivatedRoute,
    router: Router,
    appActions: ApplicationActions,
    flowManager: FlowManagerService) {
    this.route = route;
    this.router = router;
    this.appActions = appActions;
    this.flowManager = flowManager;
    this.resetPageData();
  }

  lpObject = {
    section: '',
    sdes: [{test: 'some sdes data'}]
  }

  get experiments() {
    return this.flowManager.dtmExperiments;
  }

  /**
   * @public
   * Fires on constructor and when application times out
   */
  resetPageData() {
    this.resetCartData();

    // Init params for pageview event
    this.pageviewParams = {
      application: {
        attributes: {},
        products: []
      },
      experiments: [],
      isNativeMobile: false,
      sessionId: '',
      user: {
        attributes: {
          customerOrProspect: 'prospect',
          existingCustomer: false
        }
      }
    };
  }

  /**
   * @private
   * Fires to remove existing product before addToCart event
   */
  resetCartData() {
    try {
      const hasProduct = window.allytm
        && window.allytm.ddo
        && window.allytm.ddo.get('cart.item', []).length > 0;

      if (hasProduct) {
        const id = window.allytm.ddo.get('cart.item.0.productInfo.productID');
        window.allytm.removeFromCart({ id });
      }
    } catch (e) {
      console.warn(e.message);
    }
  }

  /**
   * @private
   * Event fired after clicking next on /select-account-type
   */
  triggerEventAddToCart() {
    const accountType = this.flowManager.getAccountType();

    if (accountType) {
      const newProduct = {
        id: accountType,
        name: accountType,
        category: 'Invest',
      };

      // Check for existing product and remove if it exists
      this.resetCartData();

      // Add pageview product parameters
      this.injectProductData();

      // Fire addToCart event
      if (window.allytm) {
        window.allytm.event('addToCart', newProduct);
      }
    }
  }

  /**
   * @public
   * Event fired on the route that follows application submit
   *
   * @fires DTMService#injectAppSubmitResultData
   * @fires DTMService#resetFundingData
   */
  triggerEventPurchaseComplete(decision) {
    if (window.allytm) {
      window.allytm.event('purchaseComplete', null, { decision });
    }

    this.injectAppSubmitResultData();

    if (decision === 'pended') {
      this.resetFundingData(this.pageviewParams, this.flowManager, this.appActions);
    }
  }

  /**
   * @public
   * Event fired when the user has no activity for the alloted time and is presented the timeout modal
   */
  triggerTimeoutEvent() {
    this.resetPageData();
  }

  /**
   * @public
   * Event fired when a modal is opened
   */
  triggerModalViewEvent(title) {
    if (window.allytm) {
      const modalName = title || 'Modal';
      window.allytm.event('modalView', `${this.pageName}:${modalName}`);
    }
  }

  /**
   * @public
   * For tracking Live Person Chat
   */
  fireDigitalLayer() {
    // Live Person Chat
    if (window.lpTag !== undefined && typeof window.lpTag.newPage === 'function') {
      this.lpObject.section = this.pageName;
      window.lpTag.newPage(window.location.href, this.lpObject);
    }
  }

  /**
   * @public
   * When user clicks on some element
   *
   * @param {string} id The HTML id of the element that was clicked
   *
   * @fires DTMService#triggerEventAddToCart
   * @fires DTMService#injectOptionsData
   */
  userAction(id) {
    // If navigating to a new route
    if (id === 'next_btn' || id === 'back_btn') {
      const routeName = this.route.replace('/', '');
      switch (routeName) {
        case 'select-account-type':
          this.triggerEventAddToCart();
          break;
        case 'investment-objective':
          this.injectOptionsData();
          break;
        default:
      }
    } else if (id === 'fund_later') {
      this.resetFundingData(this.pageviewParams, this.flowManager, this.appActions);
    }
  }

  /**
   * @private
   * Gets the route title to build the Launch pageName
   *
   * @param {string} routeName
   * @param {string} key
   */
  mapRouteName(routeName, key) {
    const tempRouteName = routeName.replace('(aux:modal)', '');
    const obj = Routes.get(tempRouteName);
    return obj != null && obj[key] != null ? obj[key] : tempRouteName;
  }

  /**
   * @public
   * Sends this.pageviewParameters object to allytm pageview event
   *
   * @param {string} routeOverride
   *
   * @fires DTMService#mapRouteName
   * @fires DTMService#injectApplicationData
   * @fires DTMService#resetFundingData
   * @fires DTMService#injectDepositData
   */
  injectPageData(routeOverride) {
    this.route = routeOverride || location.pathname.replace('/ola/', '');
    const routeTitle = this.mapRouteName(`/${this.route}`, 'title');
    const appType = this.flowManager.getAppType() === 'tks' ? 'Ally Invest' : 'Ally Advisors';
    this.pageName = `${appType}:OLA:${routeTitle}`;
    const routeInjectMap = new Map([
      ['cash-allocation', [this.injectApplicationData]],
      ['', [this.injectApplicationData]],
      ['get-started', [this.injectApplicationData]],
      ['welcome-investor', [this.injectApplicationData]],
      ['select-account-type', [this.injectApplicationData]],
      ['paper-account', [this.resetFundingData]],
      ['fund-by-check', [this.injectFundingData]],
      ['fund-by-wire', [this.injectFundingData]],
      ['fund-by-invest-account', [this.injectFundingData]],
      ['fund-by-broker-receipt', [this.injectFundingData]],
      ['fund-by-broker-manual', [this.injectFundingData]],
      ['funding-choices', [this.resetFundingData]],
      ['funding-choices-streamline', [this.resetFundingData]],
      ['deposit-funds-receipt', [this.injectFundingData, this.injectDepositData]]
    ]);
    const routeInject = routeInjectMap.get(this.route.replace('/', ''));

    if (routeInject != null) {
      routeInject.forEach(func => func(this.pageviewParams, this.flowManager, this.appActions));
    }

    // Set A/B variation tests
    this.pageviewParams.experiments = this.experiments;

    // Fire pageview event
    if (window.allytm) {
      window.allytm.event('pageview', this.pageName, this.pageviewParams);
    }

    this.fireDigitalLayer();
  }

  /**
   * @private
   * Sets application GUID, device, auth, and existing Invest user on every landing page
   *
   * @param {Object} data this.pageviewParams sent to DDO on allytm pageview event
   * @param {FlowManagerService} flow getters/setters for application singletons
   * @param {ApplicationActions} actions getters/setters for Redux store
   */
  injectApplicationData(data, flow, actions) {
    const isNativeMobile = actions.getData('isMobile', false);
    const customerOrProspect = flow.isAuthed || isNativeMobile ? 'customer' : 'prospect';
    const existingCustomer = flow.isTkCustomer;

    data.isNativeMobile = isNativeMobile;
    data.sessionId = flow.guid;
    data.user = {
      attributes: {
        customerOrProspect,
        existingCustomer
      }
    }
  }

  /**
   * @private
   * Sets whether the user applied for Option on account
   */
  injectOptionsData() {
    this.pageviewParams.application.attributes.selectOptions = this.flowManager.getApplyForOption();
  }

  /**
   * @private
   * Sets the account type being applied for and whether the account is an IRA
   * when the user navigates from /select-account-type
   */
  injectProductData() {
    const products = {
      productName: this.flowManager.getAccountType(),
      productType: this.flowManager.isIra ? 'IRA' : 'Non-IRA'
    };
    this.pageviewParams.application.products[0] = products;
  }

  /**
   * @private
   * Resets Funding data if application goes pending,
   * lands on funding-choices/funding-choices-streamline
   * or user clicks the "Fund Later" button
   */
  resetFundingData(data, flow, actions) {
    flow.fundingMethod = '';
    data.application.attributes.fundingSource = '',
    data.application.attributes.depositAmount = 0;
  }

  /**
   * @private
   * Set applicationId, requestId, and decision after application submission
   */
  injectAppSubmitResultData() {
    this.pageviewParams.application.id = this.flowManager.applicationId;
    this.pageviewParams.application.attributes.requestId = this.flowManager.requestId;
    this.pageviewParams.application.attributes.selectOptions = this.flowManager.getApplyForOption();
  }

  /**
   * @public
   * Set funding method
   *
   * @param {string} source OAT, ACH, check, wire transfer, journal request, or ACATS
   */
  injectFundingData(data, flow, actions) {
    data.application.attributes.fundingSource = flow.fundingMethod;
  }

  /**
   * @private
   * Set funding source and deposit amount after successful deposit
   *
   * @param {Object} data this.pageviewParams sent to DDO on allytm pageview event
   * @param {FlowManagerService} flow getters/setters for application singletons
   * @param {ApplicationActions} actions getters/setters for Redux store
   */
  injectDepositData(data, flow, actions) {
    data.application.attributes.depositAmount = actions.getData('DepositFunds.amount', 0);
  }

  /**
   * @public
   * Dispatch error messages displayed to the user from failed validation
   *
   * @param {Array} errors field HTML IDs and validation error messages
   */
  triggerErrorEvent(errors) {
    if (window.allytm) {
      errors.forEach((err) => {
        const { id, message } = err;
        window.allytm.event('customError', message, null, { id });
      });
    }
  }
}
